import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { useNavigate } from 'react-router-dom';

interface RoleDeleteConfirmProps {
  open: boolean;
  roleCode: string;
  systemRole: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  errorMessage?: string;
  errorCode?: string;
}

/**
 * 역할 삭제 확인 다이얼로그.
 * - 시스템 역할: 삭제 버튼 disabled + 안내 메시지
 * - ROLE-409-002 (사용 중): 사용자 카운트 표시 + 사용자 관리로 이동 링크
 */
export function RoleDeleteConfirm({
  open,
  roleCode,
  systemRole,
  onClose,
  onConfirm,
  isLoading = false,
  errorMessage,
  errorCode,
}: RoleDeleteConfirmProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Esc 키 닫기
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const isInUse = errorCode === 'ROLE-409-002';

  return (
    <FormDialog
      open={open}
      title={t('role.delete.title')}
      onClose={onClose}
      onSubmit={systemRole ? onClose : onConfirm}
      submitLabel={
        systemRole
          ? t('role.delete.cancel')
          : isLoading
          ? t('role.delete.deleting', { defaultValue: '삭제 중...' })
          : t('role.delete.confirm')
      }
      cancelLabel={t('role.delete.cancel')}
    >
      <div>
        {/* 시스템 역할 경고 */}
        {systemRole && (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-warning)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-warning)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            {t('role.delete.system.disabled.tooltip')}
          </div>
        )}

        {/* 일반 삭제 확인 메시지 */}
        {!systemRole && (
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-3)' }}>
            <strong
              style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}
            >
              [{roleCode}]
            </strong>{' '}
            {t('role.delete.description', { code: roleCode })}
          </p>
        )}

        {/* 에러 — 사용 중 역할 */}
        {errorMessage && isInUse && (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            <p style={{ marginBottom: 'var(--spacing-2)' }}>{errorMessage}</p>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/users');
              }}
              style={{
                background: 'transparent',
                border: `1px solid var(--color-danger)`,
                borderRadius: 'var(--radius-sm)',
                padding: 'var(--spacing-1) var(--spacing-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('role.error.inUse.goToUsers')}
            </button>
          </div>
        )}

        {/* 일반 에러 */}
        {errorMessage && !isInUse && (
          <div
            role="alert"
            style={{
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
    </FormDialog>
  );
}
